import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigationItemWithState } from '@/entities/navigation';

import { resolveIcon, NAVIGATION_LABEL_KEYS } from '@/entities/navigation';
import { NavItem, useManualUrl } from '@/shared/ui';

import { useNavigationItem } from '../model/use-navigation-item';

interface NavigationItemProps {
  item: NavigationItemWithState;
  isCollapsed: boolean;
  onToggle: (code: string) => void;
  level?: number;
}

export const NavigationItem = ({ item, isCollapsed, onToggle, level = 0 }: NavigationItemProps) => {
  const { t } = useTranslation();
  const { hasSubItems, isActive, isExpanded, handleClick } = useNavigationItem(item);
  const Icon = resolveIcon(item.icon);
  const helpHref = useManualUrl(item.helpContentId);

  const label = useMemo(
    () =>
      t(NAVIGATION_LABEL_KEYS[item.code] ?? `listaction.MM.${item.code.toUpperCase()}`, { defaultValue: item.label }),
    [t, item.code, item.label]
  );

  return (
    <NavItem
      label={label}
      icon={Icon}
      badge={item.badge}
      isActive={isActive}
      isExpanded={isExpanded}
      isCollapsed={isCollapsed}
      hasSubItems={hasSubItems}
      helpHref={helpHref}
      helpLabel={t('gen.help.title', 'Help')}
      onClick={() => handleClick(onToggle)}>
      {item.subItems?.map((subItem) => (
        <li key={subItem.code}>
          <NavigationItem
            item={subItem as NavigationItemWithState}
            isCollapsed={isCollapsed}
            onToggle={onToggle}
            level={level + 1}
          />
        </li>
      ))}
    </NavItem>
  );
};
